import React, { useState, useEffect, useMemo } from "react";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import Grid from "@mui/material/Grid";
import { timestampConversion } from "utils/common";
import moment from "moment";

import { updateFirstLetter } from "utils/helper";
import ModalHeader from "containers/modal-header";
import { Card, CardContent, Divider, Icon } from "@mui/material";
import ModalRoot from "containers/modal/ModalRoot";

function ViewClientDetails({
  open,
  handleCloseModal,
  mode = "view",
  setIsClientDetails,
  clientDetailsData,
}) {
  //default modal fields value
  const defaultHandler = useMemo(() => {
    return {
      doctorName: null,
      bp: null,
      bloodSugar: null,

      pp: null,
      hbaic: null,
      cholestrol: null,

      hdl: null,
      vldl: null,
      ldl: null,

      sgot: null,
      sgpt: null,
      hb: null,

      d3: null,
      vitaminb12: null,
      vitamin: null,

      calcium: null,
      uricAcid: null,

      abg: null,
      t3: null,
      t4: null,
      crp: null,
      potassium: null,
      bloodUrea: null,
      bloodUreaNitrogen: null,
      transferrin: null,
      triglycerides: null,
      sodium: null,
      bilirubin: null,
      rbc: null,
      wbc: null,
      creatinine: null,
      lastPeriodDate: null,
      // Biochemical
      ongoingMedication: null,
      operated: null,
      durationMediction: null,
      allergicFrom: null,
      sufferingFrom: null,
      anyOtherCondition: null,
      rashesOnSkin: null,
      obesityInFamily: null,
      smoke: null,
      smokeAmount: null,
      alcohol: null,
      alcoholAmount: null,
      foodYouLike: null,
      foodYouDislike: null,
      waterIntake: null,
      sleepHours: null,
      habitOfEating: null,
      allergicFoods: null,
      heartiestMeal: null,
      skippedMeal: null,
      fastFoodConsumption: null,
      softDrinks: null,
      isFastingObserve: null,
      isLooseWeightTried: null,
      looseWeightProgramme: null,
      weightLost: null,
      weightTimePeriod: null,

      timestamp: new Date(),
    };
  }, []);

  const [formdata, setFormdata] = useState(defaultHandler);
  const [foodsYouEnjoyDefaultValues, setFoodsYouEnjoy] = useState([{}]);

  const hasData = (fields) => fields.some((field) => formdata[field]);

  useEffect(() => {
    if (clientDetailsData?.biochemical?.length) {
      // Sort biochemical entries by timestamp (latest first)
      const sortedBiochemical = [...clientDetailsData.biochemical].sort(
        (a, b) => (b.timestamp?.seconds ?? 0) - (a.timestamp?.seconds ?? 0)
      );

      // Pick the latest entry
      const latestBiochemical = sortedBiochemical[0];

      setFormdata({
        ...defaultHandler,
        ...latestBiochemical,
        rashesOnSkin: {
          label: latestBiochemical.rashesOnSkin,
          value: latestBiochemical.rashesOnSkin,
        },
        obesityInFamily: {
          label: latestBiochemical.obesityInFamily,
          value: latestBiochemical.obesityInFamily,
        },
        smoke: {
          label: latestBiochemical.smoke,
          value: latestBiochemical.smoke,
        },
        alcohol: {
          label: latestBiochemical.alcohol,
          value: latestBiochemical.alcohol,
        },
        heartiestMeal: {
          label: latestBiochemical.heartiestMeal,
          value: latestBiochemical.heartiestMeal,
        },
        skippedMeal: Array.isArray(latestBiochemical.skippedMeal)
          ? latestBiochemical.skippedMeal.map((item) =>
              typeof item === "string" ? { label: item, value: item } : item
            )
          : latestBiochemical.skippedMeal
          ? [
              {
                label: latestBiochemical.skippedMeal,
                value: latestBiochemical.skippedMeal,
              },
            ]
          : [],
        fastFoodConsumption: {
          label: latestBiochemical.fastFoodConsumption,
          value: latestBiochemical.fastFoodConsumption,
        },
        softDrinks: {
          label: latestBiochemical.softDrinks,
          value: latestBiochemical.softDrinks,
        },
        isFastingObserve: {
          label: latestBiochemical.isFastingObserve,
          value: latestBiochemical.isFastingObserve,
        },
        isLooseWeightTried: {
          label: latestBiochemical.isLooseWeightTried?.label ?? "",
          value: latestBiochemical.isLooseWeightTried?.value ?? "",
        },
        foodsYouEnjoy: [...(latestBiochemical?.foodsYouEnjoy || [])],
        timestamp: latestBiochemical?.timestamp?.seconds
          ? timestampConversion(latestBiochemical.timestamp)._d
          : new Date(),
      });

      const foodsYouEnjoy =
        latestBiochemical?.foodsYouEnjoy?.map((item) => ({
          value: item,
          label: item ? updateFirstLetter(item) : "",
        })) ?? [];

      setFoodsYouEnjoy(foodsYouEnjoy);
    }
  }, [clientDetailsData, defaultHandler]);

  useEffect(() => {
    if (!open) {
      setFormdata(defaultHandler);
      handleCloseModal();
    }
  }, [open, defaultHandler, handleCloseModal]);

  return (
    <ModalRoot
      variant="permanent"
      ownerState={{ openConfigurator: open }}
      width="85%"
      sx={{
        "& .MuiDrawer-paper": {
          backgroundColor: "#fff",
        },
      }}
    >
      <SuiBox
        style={{ whiteSpace: "normal !important" }}
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <ModalHeader
          mode={mode}
          title="Client's Dietary, Lifestyle & Biochemical Assessment Data
 "
        />

        <Grid item>
          <Icon
            sx={({
              typography: { size, fontWeightBold },
              palette: { dark },
            }) => ({
              fontSize: `${size.md} !important`,
              fontWeight: `${fontWeightBold} !important`,
              stroke: dark.main,
              strokeWidth: "2px",
              cursor: "pointer",
              mt: 2,
            })}
            onClick={() => {
              setIsClientDetails(false);
              setFormdata(defaultHandler);
            }}
          >
            close
          </Icon>
        </Grid>
      </SuiBox>

      <Divider />

      <SuiBox px={0} mt={2}>
        <SuiBox>
          {hasData(["doctorName", "updatedAt"]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Doctor & Record Info
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.doctorName && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Doctor Name
                      </SuiTypography>
                      <SuiTypography>{formdata.doctorName}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.updatedAt?.seconds && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Date of Record
                      </SuiTypography>
                      <SuiTypography>
                        {moment(formdata.updatedAt.seconds * 1000).format(
                          "DD MMM YYYY, h:mm A"
                        )}
                      </SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Vitals Section */}
          {hasData(["bp", "bloodSugar", "pp"]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Vitals
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.bp && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">BP</SuiTypography>
                      <SuiTypography>{formdata.bp}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.bloodSugar && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Blood Sugar
                      </SuiTypography>
                      <SuiTypography>{formdata.bloodSugar}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.pp && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">PP</SuiTypography>
                      <SuiTypography>{formdata.pp}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Blood Tests Section */}
          {hasData([
            "hbaic",
            "cholestrol",
            "hdl",
            "ldl",
            "vldl",
            "hb",
            "rbc",
            "wbc",
            "triglycerides",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Blood Tests
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.hbaic && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">HBA1C</SuiTypography>
                      <SuiTypography>{formdata.hbaic}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.cholestrol && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Cholesterol
                      </SuiTypography>
                      <SuiTypography>{formdata.cholestrol}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.hdl && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">HDL</SuiTypography>
                      <SuiTypography>{formdata.hdl}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.ldl && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">LDL</SuiTypography>
                      <SuiTypography>{formdata.ldl}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.vldl && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">VLDL</SuiTypography>
                      <SuiTypography>{formdata.vldl}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.hb && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">HB</SuiTypography>
                      <SuiTypography>{formdata.hb}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.rbc && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">RBC</SuiTypography>
                      <SuiTypography>{formdata.rbc}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.wbc && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">WBC</SuiTypography>
                      <SuiTypography>{formdata.wbc}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.triglycerides && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Triglycerides
                      </SuiTypography>
                      <SuiTypography>{formdata.triglycerides}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Vitamins & Minerals Section */}
          {hasData(["vitamin", "d3", "vitaminb12", "calcium"]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Vitamins & Minerals
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.vitamin && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">Vitamin</SuiTypography>
                      <SuiTypography>{formdata.vitamin}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.d3 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Vitamin D3
                      </SuiTypography>
                      <SuiTypography>{formdata.d3}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.vitaminb12 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Vitamin B12
                      </SuiTypography>
                      <SuiTypography>{formdata.vitaminb12}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.calcium && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">Calcium</SuiTypography>
                      <SuiTypography>{formdata.calcium}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Liver Function Section */}
          {hasData(["sgot", "sgpt", "bilirubin", "crp"]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Liver Function
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.sgot && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">SGOT</SuiTypography>
                      <SuiTypography>{formdata.sgot}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.sgpt && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">SGPT</SuiTypography>
                      <SuiTypography>{formdata.sgpt}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.bilirubin && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Bilirubin
                      </SuiTypography>
                      <SuiTypography>{formdata.bilirubin}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.crp && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">CRP</SuiTypography>
                      <SuiTypography>{formdata.crp}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Kidney Function Section */}
          {hasData([
            "uricAcid",
            "creatinine",
            "bloodUrea",
            "bloodUreaNitrogen",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Kidney Function
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.uricAcid && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Uric Acid
                      </SuiTypography>
                      <SuiTypography>{formdata.uricAcid}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.creatinine && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Creatinine
                      </SuiTypography>
                      <SuiTypography>{formdata.creatinine}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.bloodUrea && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Blood Urea
                      </SuiTypography>
                      <SuiTypography>{formdata.bloodUrea}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.bloodUreaNitrogen && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Blood Urea Nitrogen
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.bloodUreaNitrogen}
                      </SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Thyroid Section */}
          {hasData(["t3", "t4"]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Thyroid
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.t3 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">T3</SuiTypography>
                      <SuiTypography>{formdata.t3}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.t4 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">T4</SuiTypography>
                      <SuiTypography>{formdata.t4}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Other Tests Section */}
          {hasData([
            "abg",
            "transferrin",
            "potassium",
            "sodium",
            "lastPeriodDate",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Other Tests
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.abg && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">ABG</SuiTypography>
                      <SuiTypography>{formdata.abg}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.transferrin && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Transferrin
                      </SuiTypography>
                      <SuiTypography>{formdata.transferrin}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.potassium && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Potassium
                      </SuiTypography>
                      <SuiTypography>{formdata.potassium}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.sodium && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">Sodium</SuiTypography>
                      <SuiTypography>{formdata.sodium}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.lastPeriodDate && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Last Period Date
                      </SuiTypography>
                      <SuiTypography>{formdata.lastPeriodDate}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </SuiBox>

        <SuiBox>
          {/* Medication & Medical History */}
          {hasData(["ongoingMedication", "operated", "durationMediction"]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Medication & Medical History
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.ongoingMedication && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Ongoing Medication
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.ongoingMedication}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.operated && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Operated?
                      </SuiTypography>
                      <SuiTypography>{formdata.operated}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.durationMediction && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Duration & Medication
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.durationMediction}
                      </SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Allergies & Conditions */}
          {hasData([
            "allergicFrom",
            "sufferingFrom",
            "anyOtherCondition",
            "allergicFoods",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Allergies & Conditions
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.allergicFrom?.length > 0 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Allergic From
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.allergicFrom.join(", ")}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.sufferingFrom?.length > 0 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Suffering From
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.sufferingFrom.join(", ")}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.anyOtherCondition && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Any Other Condition?
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.anyOtherCondition}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.allergicFoods && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Allergic Foods
                      </SuiTypography>
                      <SuiTypography>{formdata.allergicFoods}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Lifestyle Habits */}
          {hasData([
            "smoke",
            "smokeAmount",
            "alcohol",
            "alcoholAmount",
            "waterIntake",
            "sleepHours",
            "rashesOnSkin",
            "obesityInFamily",
            "habitOfEating",
            "isFastingObserve",
            "observeFasting",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Lifestyle Habits
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.smoke?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Do You Smoke?
                      </SuiTypography>
                      <SuiTypography>{formdata.smoke.value}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.smokeAmount && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Cigarettes per day
                      </SuiTypography>
                      <SuiTypography>{formdata.smokeAmount}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.alcohol?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Do You Consume Alcohol?
                      </SuiTypography>
                      <SuiTypography>{formdata.alcohol.value}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.alcoholAmount && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Alcohol Consumption
                      </SuiTypography>
                      <SuiTypography>{formdata.alcoholAmount}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.waterIntake && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Daily Water Intake
                      </SuiTypography>
                      <SuiTypography>{formdata.waterIntake}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.sleepHours && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Sleep Hours
                      </SuiTypography>
                      <SuiTypography>{formdata.sleepHours}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.rashesOnSkin?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Rashes on Skin
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.rashesOnSkin.value}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.obesityInFamily?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Obesity in Family
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.obesityInFamily.value}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.habitOfEating && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Habit of Eating Out
                      </SuiTypography>
                      <SuiTypography>{formdata.habitOfEating}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.isFastingObserve?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Observe Fasting?
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.isFastingObserve.value}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.observeFasting && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Fasting Frequency
                      </SuiTypography>
                      <SuiTypography>{formdata.observeFasting}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Diet Preferences */}
          {hasData([
            "foodYouLike",
            "foodYouDislike",
            "heartiestMeal",
            "skippedMeal",
            "foodsYouEnjoy",
            "fastFoodConsumption",
            "softDrinks",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Diet Preferences
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.foodYouLike && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Food You Like
                      </SuiTypography>
                      <SuiTypography>{formdata.foodYouLike}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.foodYouDislike && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Food You Dislike
                      </SuiTypography>
                      <SuiTypography>{formdata.foodYouDislike}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.heartiestMeal?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Heartiest Meal
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.heartiestMeal.value}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.skippedMeal && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Skipped Meal
                      </SuiTypography>
                      <SuiTypography>
                        {Array.isArray(formdata.skippedMeal)
                          ? formdata.skippedMeal
                              .map((item) =>
                                typeof item === "string"
                                  ? item
                                  : item.label || item.value
                              )
                              .join(", ")
                          : formdata.skippedMeal.label ||
                            formdata.skippedMeal.value ||
                            formdata.skippedMeal}
                      </SuiTypography>
                    </Grid>
                  )}

                  {formdata.foodsYouEnjoy?.length > 0 && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Foods You Enjoy
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.foodsYouEnjoy.join(", ")}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.fastFoodConsumption?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Fast Food Consumption
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.fastFoodConsumption.value}
                      </SuiTypography>
                    </Grid>
                  )}

                  {formdata.softDrinks?.value && (
                    <Grid item xs={12} md={3}>
                      <SuiTypography variant="subtitle2">
                        Soft Drinks / Packed Juices
                      </SuiTypography>
                      <SuiTypography>{formdata.softDrinks.value}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Weight Loss History */}
          {hasData([
            "isLooseWeightTried",
            "looseWeightProgramme",
            "weightLost",
            "weightTimePeriod",
          ]) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Weight Loss History
                </SuiTypography>
                <Grid container spacing={2}>
                  {formdata.isLooseWeightTried?.value && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Tried to Lose Weight
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.isLooseWeightTried.value}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.looseWeightProgramme && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Programme Type
                      </SuiTypography>
                      <SuiTypography>
                        {formdata.looseWeightProgramme}
                      </SuiTypography>
                    </Grid>
                  )}
                  {formdata.weightLost && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Weight Lost (Kg)
                      </SuiTypography>
                      <SuiTypography>{formdata.weightLost}</SuiTypography>
                    </Grid>
                  )}
                  {formdata.weightTimePeriod && (
                    <Grid item xs={12} md={4}>
                      <SuiTypography variant="subtitle2">
                        Duration (Months)
                      </SuiTypography>
                      <SuiTypography>{formdata.weightTimePeriod}</SuiTypography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Timestamp */}
          {/* {formdata.timestamp && (
            <Card sx={{mb:2}}>
              <CardContent>
                <SuiTypography variant="h5" mb={2}>
                  Record Timestamp
                </SuiTypography>
                <SuiTypography>{formdata.timestamp}</SuiTypography>
              </CardContent>
            </Card>
          )} */}
        </SuiBox>
      </SuiBox>
    </ModalRoot>
  );
}

export default ViewClientDetails;
